// householdsClient — the wire for `/api/households` (§7.4) and
// `/api/households/:householdId` (§7.5), plus the pure URL logic behind the
// two household pages.
//
// The types are W0's, unchanged, in `sentItemsClient.ts`. Nothing here
// re-declares a shape; this file is encoding and vocabulary only:
//
//   • the query encoding, written once so the page, the tests and the fetcher
//     cannot disagree about what a filter is called on the wire (T27 moved
//     every household filter server-side — there is no client-side fallback);
//   • the `householdId` grammar, which is the one thing on these two pages
//     that is easy to get subtly wrong: an id is EITHER a caseId or the
//     literal `group:<groupId>` of an applicant group that has no case yet
//     (T15). Both forms are ONE path segment, so both are encoded whole;
//   • the labels and tones for `relationship` and `standing.tone`, so the list
//     row and the detail header say the same words about the same fact.
//
// What this file deliberately does NOT do is derive a standing line, a stage,
// or a next step. §8.4 is a server-side function over the rows THIS VIEWER may
// see (T5); a client that recomputed it would compute it over a filtered set
// and quietly disagree with the API about what is happening to a household.

import { api } from '@/lib/api';
import { describeWhere } from '@/lib/viewDocs';
import { sendContextQuery } from '@/lib/sendContext';
import type {
  HouseholdDetailResponse,
  HouseholdListResponse,
  HouseholdListRow,
  HouseholdRelationship,
  HouseholdsFilter,
  HouseholdsListParams,
  WhereItStands,
} from '@/lib/sentItemsClient';

// ---------------------------------------------------------------------------
// The `householdId` grammar (T15)
// ---------------------------------------------------------------------------

/** The prefix that marks an applicant group with no case behind it yet. */
export const GROUP_ID_PREFIX = 'group:';

/** True for `group:<groupId>` — a household that exists only as an
 *  application. NOTHING may try to load a docket for one of these (T15). */
export function isPreCaseHouseholdId(householdId: string | null | undefined): boolean {
  return typeof householdId === 'string' && householdId.startsWith(GROUP_ID_PREFIX);
}

/** The groupId inside a `group:<groupId>` id; null for a caseId. */
export function groupIdFromHouseholdId(householdId: string | null | undefined): string | null {
  if (!isPreCaseHouseholdId(householdId)) return null;
  const groupId = (householdId as string).slice(GROUP_ID_PREFIX.length);
  return groupId.length > 0 ? groupId : null;
}

/** The id of a household we know only as an application group. */
export function householdIdForGroup(groupId: string): string {
  return `${GROUP_ID_PREFIX}${groupId}`;
}

/** The route to a household's file. The id can carry a `:` — encode it whole. */
export function householdPath(householdId: string): string {
  return `/households/${encodeURIComponent(householdId)}`;
}

/**
 * Where `/applications` goes now (§8.1): the households list, narrowed to the
 * people who are applying.
 *
 * Exported rather than written into the route, so the redirect and the test
 * that guards it cannot drift — this URL is in bookmarks, in the Send hub's
 * "Lease" tile and in a year of Linear comments, and §9 is explicit that the
 * redirect is not optional.
 */
// applying AND approved: the old Applications page listed every active
// application, and an approved-not-yet-converted group (the lease-building
// window) must not vanish from the landing this URL replaces (WEL-147).
export const APPLICATIONS_REDIRECT = '/households?filter=motion&relationship=applying,approved';

/** The lease builder for an application group — the ONE builder route, which
 *  this work does not touch (§8.1). */
export function leaseBuilderPath(groupId: string): string {
  return `/applications/${encodeURIComponent(groupId)}/lease`;
}

/**
 * "Send…" for one household row (sol finding 10).
 *
 * The hub with the household attached, so the surface it opens can preselect
 * them. Before this, the link was a bare `/send` and W6-NOTES said what that
 * cost: staff landed on a picker with the household they had just been looking
 * at unselected. The names and the address travel so the hub can SAY who it is
 * for without a second request; the ids travel so the pickers can act on it.
 */
export function sendHubPathFor(row: HouseholdListRow): string {
  const where = describeWhere(row.propertyLabel, row.unitLabel);
  return `/send${sendContextQuery({
    householdId: row.householdId,
    ...(row.groupId ? { groupId: row.groupId } : {}),
    ...(row.propertyId ? { propertyId: row.propertyId } : {}),
    ...(row.unitId ? { unitId: row.unitId } : {}),
    name: row.name,
    ...(where ? { where } : {}),
  })}`;
}

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** The three list filters, in chip order. Default `motion` (Q10). */
export const HOUSEHOLD_FILTERS: readonly HouseholdsFilter[] = ['motion', 'all', 'closed'];

/** What each filter answers, in the words the chip shows. */
export const HOUSEHOLD_FILTER_LABEL: Record<HouseholdsFilter, string> = {
  motion: 'In motion',
  all: 'All',
  closed: 'Closed',
};

/** The second line under each chip, so a filter says what it contains. */
export const HOUSEHOLD_FILTER_HINT: Record<HouseholdsFilter, string> = {
  motion: 'something is moving',
  all: 'every household',
  closed: 'nothing left to do',
};

/** Every relationship, in select order. */
export const HOUSEHOLD_RELATIONSHIPS: readonly HouseholdRelationship[] = [
  'applying',
  'approved',
  'tenant',
  'past',
  'none',
];

/**
 * What AppFolio says this household is to us — a COLUMN, not a stage (Q10).
 *
 * `none` is a household with no application behind it at all (a tenancy we
 * only ever sent a renewal to, say). It is said as a fact about the record
 * rather than as a gap: "no application" is true, "unknown" would not be.
 */
export const RELATIONSHIP_LABEL: Record<HouseholdRelationship, string> = {
  applying: 'Applying',
  approved: 'Approved',
  tenant: 'Tenant',
  past: 'Past resident',
  none: 'No application',
};

/** Pill tones — documented values only (T29: there is no `primary` variant and
 *  `warn` is not a tone). */
export const RELATIONSHIP_TONE: Record<HouseholdRelationship, string> = {
  applying: 'info',
  approved: 'accent',
  tenant: 'success',
  past: 'neutral',
  none: 'neutral',
};

/** The standing line's dot tone. `empty` is muted rather than a colour: a
 *  household nothing has been sent to is not a warning. */
export function standingDotTone(tone: WhereItStands['tone']): string {
  switch (tone) {
    case 'blocked':
      return 'danger';
    case 'active':
      return 'info';
    case 'done':
      return 'success';
    case 'empty':
      return 'muted';
  }
}

// ---------------------------------------------------------------------------
// URL state (§8.2 — "all in the URL", so a filtered list is a link)
// ---------------------------------------------------------------------------

function isFilter(value: string | null): value is HouseholdsFilter {
  return value === 'motion' || value === 'all' || value === 'closed';
}

function isRelationship(value: string | null): value is HouseholdRelationship {
  return (HOUSEHOLD_RELATIONSHIPS as readonly string[]).includes(value ?? '');
}

/**
 * The URL, read as list params.
 *
 * Unknown values are DROPPED rather than forwarded: a hand-edited
 * `?relationship=nonsense` should show the default list, not ask the server
 * what it makes of nonsense. `filter` always comes back set — `motion` is the
 * default and the server is told so explicitly, so "no filter" never has to
 * mean two different things on two sides of the wire.
 */
export function householdParamsFromSearch(search: URLSearchParams): HouseholdsListParams {
  const filterParam = search.get('filter');
  const relationship = search.get('relationship');
  const q = search.get('q') ?? '';

  const params: HouseholdsListParams = {
    filter: isFilter(filterParam) ? filterParam : 'motion',
  };
  if (relationship) {
    // Comma-separated set; unknown values are dropped rather than sent (a
    // hand-edited `?relationship=nonsense` shows the default list). WEL-147.
    const values = relationship.split(',').map((v) => v.trim()).filter(isRelationship);
    if (values.length > 0) params.relationship = values;
  }
  if (q.trim()) params.q = q;
  return params;
}

/**
 * A partial update to the filters.
 *
 * `Partial<HouseholdsListParams>` is NOT this type: under
 * `exactOptionalPropertyTypes` an optional property still refuses an explicit
 * `undefined`, and "clear the relationship" is exactly
 * `{ relationship: undefined }`.
 */
export type HouseholdsFilterPatch = {
  [K in keyof HouseholdsListParams]?: HouseholdsListParams[K] | undefined;
};

/** The inverse: params written back onto a URLSearchParams, in place. The
 *  DEFAULT filter is left out of the URL, so `/households` is the plain link. */
export function applyHouseholdParamsToSearch(
  search: URLSearchParams,
  params: HouseholdsFilterPatch,
): URLSearchParams {
  const set = (key: string, value: string | null | undefined) => {
    if (value) search.set(key, value);
    else search.delete(key);
  };
  set('filter', params.filter && params.filter !== 'motion' ? params.filter : null);
  set('relationship', params.relationship && params.relationship.length > 0 ? params.relationship.join(',') : null);
  set('q', params.q?.trim() ? params.q : null);
  return search;
}

/** True when anything narrows the list beyond the default view. */
export function hasActiveHouseholdFilters(params: HouseholdsFilterPatch): boolean {
  return Boolean(params.relationship?.length || params.q?.trim() || (params.filter && params.filter !== 'motion'));
}

// ---------------------------------------------------------------------------
// The wire
// ---------------------------------------------------------------------------

/** The §7.4 query encoding, as one pure function. Every key is omitted when
 *  unset — an empty `relationship=` is a filter the server would have to guess
 *  the meaning of. */
export function householdsQuery(params: HouseholdsListParams = {}): string {
  const sp = new URLSearchParams();
  if (params.filter) sp.set('filter', params.filter);
  if (params.q && params.q.trim()) sp.set('q', params.q.trim());
  if (params.relationship && params.relationship.length > 0) sp.set('relationship', params.relationship.join(','));
  if (params.cursor) sp.set('cursor', params.cursor);
  if (params.limit != null) sp.set('limit', String(params.limit));
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** GET /api/households (§7.4). The SERVER filters, searches and sorts (T27). */
export function fetchHouseholds(
  params: HouseholdsListParams = {},
): Promise<HouseholdListResponse> {
  return api.get<HouseholdListResponse>(`/api/households${householdsQuery(params)}`);
}

/**
 * GET /api/households/:householdId (§7.5).
 *
 * The id is a caseId OR `group:<groupId>` and therefore carries a colon, so it
 * is encoded as ONE path segment — the same way an itemId is.
 */
export function fetchHousehold(householdId: string): Promise<HouseholdDetailResponse> {
  return api.get<HouseholdDetailResponse>(
    `/api/households/${encodeURIComponent(householdId)}`,
  );
}
